import React, { FC } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { BhiDialog } from 'src/UIKit';
import { Plan } from 'src/modules/plans/types';
import AdjustFreeCreditsDialog from '../AdjustFreeCreditsDialog';
import { ChangePlanDialogViewModel, SubscriptionPlanChangePreview } from '../../../types';

const formatCredits = (value: number) => value.toLocaleString();
const formatUsd = (value: number) => `$${value.toLocaleString(undefined, { maximumFractionDigits: 3 })}`;

const SummaryRow: FC<{ label: string; before: string; after?: string }> = ({ label, before, after }) => (
  <Stack direction="row" gap={1}>
    <Typography variant="body2" color="text.secondary" sx={{ width: 160 }}>
      {label}
    </Typography>
    <Typography variant="body2">
      {before}
      {after !== undefined && ` → ${after}`}
    </Typography>
  </Stack>
);

const shortfallText = (preview: SubscriptionPlanChangePreview) =>
  `Credits used this period exceed the new plan by ${formatCredits(preview.overageCredits)}.`;

const OverageNotice: FC<{ plan: Plan; preview: SubscriptionPlanChangePreview }> = ({ plan, preview }) => {
  if (preview.overageCredits <= 0) return null;

  if (plan.pricePerOverageCredit <= 0) {
    return (
      <Alert severity="info">
        {shortfallText(preview)} The new plan has no overage price, so the difference is not charged
        and free credits are not spent on it.
      </Alert>
    );
  }

  return (
    <Alert severity="warning">
      <Stack gap={1}>
        <Typography variant="body2">
          {shortfallText(preview)} The difference is billed as overage credits at{' '}
          {formatUsd(plan.pricePerOverageCredit)} per credit. Free credits (
          {formatCredits(preview.freeCredits)} available) cover the overage before anything is
          charged.
        </Typography>
        <Typography variant="body2">
          {preview.overageCreditsCharged > 0
            ? `Estimated charge: ${formatCredits(preview.overageCreditsCharged)} credits, ` +
              `${formatUsd(preview.overagePrice)}. Usage after the change adds to it.`
            : 'Free credits cover the whole difference, nothing will be charged for it.'}
        </Typography>
      </Stack>
    </Alert>
  );
};

const OverageConfirm: FC<{
  preview: SubscriptionPlanChangePreview;
  busy: boolean;
  onBack: () => void;
  onChangeWithOverage: () => void;
  onAddFreeCreditsAndChange: () => void;
}> = ({ preview, busy, onBack, onChangeWithOverage, onAddFreeCreditsAndChange }) => (
  <Stack gap={2}>
    <Alert severity="warning">
      <Stack gap={1}>
        <Typography variant="body2" fontWeight="fontWeightMedium">
          Not enough credits for the new plan
        </Typography>
        <Typography variant="body2">
          {shortfallText(preview)} The company has {formatCredits(preview.freeCredits)} free credits
          {preview.freeCreditsAppliedToPreviousPeriod > 0 &&
            `, ${formatCredits(preview.freeCreditsAppliedToPreviousPeriod)} of them go to the previous period's overage first`}
          , they cover {formatCredits(preview.freeCreditsApplied)}. The remaining{' '}
          {formatCredits(preview.overageCreditsCharged)} credits will be charged as overage (
          {formatUsd(preview.overagePrice)}).
        </Typography>
        <Typography variant="body2">
          Add {formatCredits(preview.freeCreditsNeeded)} free credits first so that nothing is
          charged, or change the plan as it is.
        </Typography>
      </Stack>
    </Alert>
    <Stack direction="row" justifyContent="flex-end" gap={1} flexWrap="wrap">
      <Button onClick={onBack} disabled={busy}>
        Back
      </Button>
      <Button variant="outlined" onClick={onChangeWithOverage} disabled={busy}>
        Change and charge overage
      </Button>
      <Button variant="contained" onClick={onAddFreeCreditsAndChange} disabled={busy}>
        Add free credits and change
      </Button>
    </Stack>
  </Stack>
);

const ChangePlanDialogView: FC<ChangePlanDialogViewModel> = ({
  open,
  subscription,
  company,
  plans,
  selectedPlan,
  preview,
  previewLoading,
  submitting,
  isOverageConfirmOpen,
  isAdjustFreeCreditsOpen,
  adjustFreeCreditsFormData,
  onSelectPlan,
  onRetryPreview,
  onBackFromOverageConfirm,
  onChangeWithOverage,
  onAddFreeCreditsAndChange,
  onCloseAdjustFreeCredits,
  onFreeCreditsAdded,
  close,
  submit,
}) => {
  const current = subscription?.plan;
  const busy = previewLoading || submitting;
  const invoicesPlan = !!selectedPlan && !!preview && preview.planPriceInvoiced > 0;

  return (
    <BhiDialog isOpen={open} onClose={submitting ? undefined : close} title="Change Plan" maxWidth="sm" fullWidth>
      <Stack gap={2} sx={{ padding: '0 20px 20px' }}>
        <FormControl fullWidth sx={{ marginTop: 1 }}>
          <InputLabel id="change-plan-new-plan">New plan</InputLabel>
          <Select
            labelId="change-plan-new-plan"
            MenuProps={{ disableScrollLock: true }}
            value={selectedPlan?.id ?? ''}
            label="New plan"
            disabled={isOverageConfirmOpen || submitting}
            onChange={e => onSelectPlan(e.target.value)}
          >
            {[...plans]
              .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
              .map(p => (
                <MenuItem value={p.id} key={p.id}>
                  {p.name} (${p.price})
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {current && (
          <Stack gap={0.5}>
            <SummaryRow label="Plan" before={current.name} after={selectedPlan?.name} />
            <SummaryRow
              label="Credits"
              before={formatCredits(subscription?.credits ?? current.credits)}
              after={selectedPlan && formatCredits(selectedPlan.credits)}
            />
            <SummaryRow
              label="Price"
              before={formatUsd(subscription?.price ?? current.price)}
              after={selectedPlan && formatUsd(selectedPlan.price)}
            />
            <SummaryRow
              label="Overage price"
              before={formatUsd(subscription?.pricePerOverageCredit ?? current.pricePerOverageCredit)}
              after={selectedPlan && formatUsd(selectedPlan.pricePerOverageCredit)}
            />
            <SummaryRow
              label="Remaining credits"
              before={formatCredits(subscription?.creditsRemaining ?? 0)}
              after={preview && formatCredits(preview.creditsRemaining)}
            />
          </Stack>
        )}

        {previewLoading && <CircularProgress size={24} />}
        {selectedPlan && !preview && !previewLoading && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetryPreview}>
                Retry
              </Button>
            }
          >
            The change could not be estimated.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Start and end dates, auto-renew and the usage of this period stay as they are.
          {!invoicesPlan && ' No invoice is created, bill any price difference manually.'}
        </Typography>

        {invoicesPlan && (
          <Alert severity="warning">
            The current plan of this subscription was never invoiced. Right after the change an
            invoice for {formatUsd(preview.planPriceInvoiced)}, the full price of {selectedPlan.name},
            is created as a draft to be charged manually. Cancel to keep the current plan.
          </Alert>
        )}

        {isOverageConfirmOpen && preview ? (
          <OverageConfirm
            preview={preview}
            busy={busy}
            onBack={onBackFromOverageConfirm}
            onChangeWithOverage={onChangeWithOverage}
            onAddFreeCreditsAndChange={onAddFreeCreditsAndChange}
          />
        ) : (
          <>
            {selectedPlan && preview && <OverageNotice plan={selectedPlan} preview={preview} />}
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button onClick={close} disabled={submitting}>
                Cancel
              </Button>
              <Button variant="contained" disabled={!selectedPlan || !preview || busy} onClick={submit}>
                {invoicesPlan ? 'Change Plan and Invoice' : 'Change Plan'}
              </Button>
            </Stack>
          </>
        )}
      </Stack>

      <AdjustFreeCreditsDialog
        open={isAdjustFreeCreditsOpen}
        company={company}
        initialFormData={adjustFreeCreditsFormData}
        onClose={onCloseAdjustFreeCredits}
        onAdjusted={onFreeCreditsAdded}
      />
    </BhiDialog>
  );
};

export default ChangePlanDialogView;
